import * as microsoftTeams from '@microsoft/teams-js';
import { api } from './client';
import type {
  DashboardDateRange,
  DashboardSortField,
  DashboardSortOrder,
  RequestCounts,
  RequestSummary,
} from './requests';
import type { RequestStatus } from '@pr-app/shared';

export interface Reviewer {
  id: string;
  aadId: string;
  name: string;
  email: string;
  addedBy: string;
  active: boolean;
  decisionsCount: number;
}

export interface DirectoryUser {
  aadId: string;
  name: string;
  email: string;
}

/** Wireframe sheet 06, A1 — member list with decisions-made count. */
export const getReviewers = (): Promise<Reviewer[]> => api<Reviewer[]>('/api/admin/reviewers');

/** Server-side directory search backing the people picker — never free text. */
export const searchDirectory = (query: string): Promise<DirectoryUser[]> =>
  api<DirectoryUser[]>(`/api/admin/directory/search?q=${encodeURIComponent(query)}`);

export const addReviewer = (aadId: string): Promise<Reviewer> =>
  api<Reviewer>('/api/admin/reviewers', { method: 'POST', body: JSON.stringify({ aadId }) });

export const removeReviewer = (aadId: string): Promise<void> =>
  api<void>(`/api/admin/reviewers/${encodeURIComponent(aadId)}`, { method: 'DELETE' });

/**
 * Who may reach the Admin tab (#156, wireframe A1 note 2). No `decisionsCount`
 * or `active` flag here — unlike the reviewer group, `admin.aadIds` is a plain
 * id list with no local table, so a name/email comes from the directory.
 */
export interface Administrator {
  aadId: string;
  name: string;
  email: string;
}

export const getAdministrators = (): Promise<Administrator[]> =>
  api<Administrator[]>('/api/admin/administrators');

export const addAdministrator = (aadId: string): Promise<Administrator> =>
  api<Administrator>('/api/admin/administrators', {
    method: 'POST',
    body: JSON.stringify({ aadId }),
  });

export const removeAdministrator = (aadId: string): Promise<void> =>
  api<void>(`/api/admin/administrators/${encodeURIComponent(aadId)}`, { method: 'DELETE' });

/** One row of the insert-only trail — system events and human ones share this shape. */
export interface AuditEntry {
  id: string;
  /** Null for admin-side actions, which belong to no single request. */
  requestId: string | null;
  /** The human PR-#### reference (wireframe A4's Request column). Null alongside requestId. */
  requestNo: number | null;
  action: string;
  actorAadId: string;
  actorName: string;
  actorEmail: string | null;
  comment: string | null;
  createdAt: string;
}

export interface AuditLogPage {
  entries: AuditEntry[];
  total: number;
  page: number;
  pageSize: number;
}

/** What the table is currently showing — and therefore exactly what an export contains. */
export interface AuditLogFilter {
  /** Free-text across actor name, comment and action. */
  q?: string;
  /** Wireframe A4's "Action:" filter — exactly one action, or every action when unset. */
  action?: string;
  /** Wireframe A4's date-range filter — inclusive, YYYY-MM-DD. */
  from?: string;
  to?: string;
}

export interface AuditLogQuery extends AuditLogFilter {
  page?: number;
}

function auditQueryString({ q, action, from, to, page }: AuditLogQuery): string {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (action) params.set('action', action);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (page !== undefined && page > 1) params.set('page', String(page));
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Server-side paging and search (#51) — the log only grows, so it is never fetched whole. */
export const getAuditLog = (query: AuditLogQuery = {}): Promise<AuditLogPage> =>
  api<AuditLogPage>(`/api/admin/audit${auditQueryString(query)}`);

/**
 * The .xlsx bytes for the CURRENT filter, not the whole table. The shared
 * `api()` helper assumes a JSON body, so this reads the response as a blob the
 * same way `fetchAttachmentBlob` does for authenticated downloads.
 */
export async function exportAuditLog(filter: AuditLogFilter = {}): Promise<Blob> {
  const token = await microsoftTeams.authentication.getAuthToken();
  const res = await fetch(`/api/admin/audit/export${auditQueryString(filter)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Could not export the audit log (${String(res.status)})`);
  return res.blob();
}

/**
 * The settings an administrator can change without a deployment (#52,
 * wireframe A3). Always complete — a setting nobody has touched reads back as
 * its shipped default.
 */
export interface AppSettings {
  /** Days of inactivity before a draft is removed. */
  draftExpiryDays: number;
  /** Bytes, not megabytes — the panel converts for display. */
  maxAttachmentSizeBytes: number;
  maxAttachmentsPerRequest: number;
}

/** Send only what is changing: an unchanged field writes no audit row. */
export type AppSettingsUpdate = Partial<AppSettings>;

export const getAppSettings = (): Promise<AppSettings> =>
  api<AppSettings>('/api/admin/settings/app');

export const updateAppSettings = (update: AppSettingsUpdate): Promise<AppSettings> =>
  api<AppSettings>('/api/admin/settings/app', {
    method: 'PUT',
    body: JSON.stringify(update),
  });

/** One live D365 call, timed. An unhealthy result is a successful response. */
export interface D365ConnectionResult {
  healthy: boolean;
  latencyMs: number;
  /** ISO timestamp of when the test ran. */
  testedAt: string;
  /** Present only when unhealthy. Truncated server-side. */
  error?: string;
}

export const testD365Connection = (): Promise<D365ConnectionResult> =>
  api<D365ConnectionResult>('/api/admin/settings/test-connection', { method: 'POST' });

/**
 * The always-visible D365 connection facts (#192, wireframe A3) — no live
 * call, just what the server already knows: which environment it is pointed
 * at, whether the shared service account has a valid cached token, and the
 * timing of the most recent successful master-data call.
 */
export interface D365Status {
  /** Host name of the configured D365 environment, e.g. "acme-uat.operations.dynamics.com". */
  environment: string;
  serviceAccount: {
    connected: boolean;
    /** Null when no token is cached yet — nothing has called D365 in this process. */
    tokenValidForMinutes: number | null;
  };
  lastMasterDataCall: { at: string; durationMs: number } | null;
}

export const getD365Status = (): Promise<D365Status> =>
  api<D365Status>('/api/admin/settings/d365-status');

/* -------------------------------------------------------------------------- *
 * Notification templates (#49, wireframe A2)
 * -------------------------------------------------------------------------- */

/**
 * The set is closed, per the SOW — there is no "create template" in the
 * admin UI. #237 grew it from two to three: `approved` is the immediate
 * "your request was approved" notice, independent of D365 PR creation.
 */
export type NotificationTemplateName = 'prCreated' | 'rejected' | 'approved';

export interface NotificationTemplateToken {
  /** Written `{{name}}` in the subject or body. */
  name: string;
  description: string;
  /** What "Send test to me" substitutes. */
  sample: string;
}

export interface NotificationTemplateContent {
  subject: string;
  body: string;
  /** A2 note 4 — off keeps the wording and sends nothing. */
  teamsEnabled: boolean;
  emailEnabled: boolean;
}

export interface NotificationTemplate extends NotificationTemplateContent {
  name: NotificationTemplateName;
  label: string;
  /**
   * The allow-list the API validates against, sent with the template so the
   * panel can list the tokens and check them before a save round trip. The
   * server checks the same thing — a drift here is a worse error message, never
   * a template that should have been refused.
   */
  tokens: NotificationTemplateToken[];
}

export interface NotificationTemplatesResponse {
  templates: NotificationTemplate[];
}

export const getNotificationTemplates = (): Promise<NotificationTemplatesResponse> =>
  api<NotificationTemplatesResponse>('/api/admin/notification-templates');

/** Both templates go together: the panel has one Save button. */
export type NotificationTemplatesUpdate = Record<
  NotificationTemplateName,
  NotificationTemplateContent
>;

export const updateNotificationTemplates = (
  update: NotificationTemplatesUpdate,
): Promise<NotificationTemplatesResponse> =>
  api<NotificationTemplatesResponse>('/api/admin/notification-templates', {
    method: 'PUT',
    body: JSON.stringify(update),
  });

export interface SendTestNotificationResult {
  sentAt: string;
  teamsSent: boolean;
  emailSent: boolean;
  subject: string;
  body: string;
  /**
   * Present only when a channel failed. Graph refusing is a 200 — the same
   * shape as the D365 connection test, so the reason survives the error
   * handler's 5xx message stripping and reaches the one person who can fix it.
   */
  error?: string;
}

/** Sends the wording currently on screen — unsaved — to the signed-in administrator. */
export const sendTestNotification = (
  template: NotificationTemplateContent & { name: NotificationTemplateName },
): Promise<SendTestNotificationResult> =>
  api<SendTestNotificationResult>('/api/admin/notification-templates/test', {
    method: 'POST',
    body: JSON.stringify(template),
  });

/**
 * #224 — an admin's "Dashboard" is org-wide: every non-draft request, purely
 * for oversight, with no action capability at all. Same request/response
 * shape as `listRequests`/`getReviewDashboard` (`RequestSummary`,
 * `RequestCounts`) so `Dashboard.tsx` can drive any of the three data sources
 * with no branching beyond which one it calls — the only addition is the
 * `decidedBy*`/`decisionAction`/`decidedAt` fields each row also carries.
 */
export interface AdminDashboardParams {
  q?: string;
  status?: RequestStatus[];
  company?: string[];
  range?: DashboardDateRange;
  from?: string;
  to?: string;
  sort?: DashboardSortField;
  order?: DashboardSortOrder;
  page?: number;
  pageSize?: number;
}

export interface AdminDashboardResult {
  requests: RequestSummary[];
  total: number;
  page: number;
  pageSize: number;
  counts: RequestCounts;
  companies: string[];
}

function buildAdminDashboardQuery(params: AdminDashboardParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.status && params.status.length > 0) search.set('status', params.status.join(','));
  if (params.company && params.company.length > 0) search.set('company', params.company.join(','));
  if (params.range) search.set('range', params.range);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.sort) search.set('sort', params.sort);
  if (params.order) search.set('order', params.order);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const getAdminDashboard = (
  params: AdminDashboardParams = {},
): Promise<AdminDashboardResult> =>
  api<AdminDashboardResult>(`/api/admin/dashboard${buildAdminDashboardQuery(params)}`);
